import { Formik } from "formik";
import React from "react";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";

import AutocompleteFilter from "@components/Forms/FormInputs/AutocompleteFilter";
import Checkbox from "@components/Forms/FormInputs/CheckBox";

import { IContactFilter } from "@store/filters/filter.types";
import { PopupActions } from "@store/popupStore";

import FilterFieldLabel from "./FilterFieldLabel";
import FilterStyles from "./FilterStyles";

type ContactFilterDrawerPropertiesType = {
  filters: IContactFilter;
  isOpen: boolean;
  resetFilters: () => void;
  setFilters: (newFilterState: Partial<IContactFilter>) => void;
  setOpen: (isOpen: boolean) => void;
};

const ContactFilterDrawer = React.memo(
  ({ filters, isOpen, resetFilters, setFilters, setOpen }: ContactFilterDrawerPropertiesType) => {
    const handleFilterChange = (newFilterState: Partial<IContactFilter>) => {
      const preppedValues = {
        hasAttendedConference: filters.hasAttendedConference,
        hasVerifiedEmail: filters.hasVerifiedEmail,
        jobTitles: filters.jobTitles,
        keywords: filters.keywords,
        locations: filters.locations,
        ...newFilterState,
      };
      PopupActions.openAlert({
        duration: 1000,
        message: "Filter Applied",
        severity: "success",
      });
      setFilters(preppedValues);
    };

    return (
      <Drawer
        anchor="top"
        ModalProps={{
          BackdropProps: {
            style: {
              backgroundColor: "rgba(0, 0, 0, 0.70)",
            },
          },
        }}
        onClose={() => setOpen(false)}
        open={isOpen}
        sx={{
          ".MuiDrawer-paper": {
            overflow: "visible",
          },
        }}
        transitionDuration={300}
      >
        <Formik
          enableReinitialize
          initialValues={filters}
          onSubmit={() => {
            /* Do Nothing */
          }}
        >
          {({ values }) => (
            <Box sx={FilterStyles.drawer}>
              <Box sx={FilterStyles.headerContainer}>
                <Icon
                  fontSize="large"
                  onClick={() => setOpen(false)}
                  sx={{
                    "&:hover": {
                      opacity: 1,
                    },
                    cursor: "pointer",
                    opacity: 0.8,
                  }}
                >
                  <CloseRoundedIcon fontSize="large" />
                </Icon>
                <Typography variant="h4">Contact Filters</Typography>
                <Button
                  color="warning"
                  endIcon={<RestartAltRoundedIcon />}
                  onClick={() => resetFilters()}
                  size="small"
                  variant="contained"
                >
                  Reset Filters
                </Button>
              </Box>
              <Box>
                <Box sx={FilterStyles.container}>
                  <Box sx={{ width: { md: "31%", xs: "100%" } }}>
                    <FilterFieldLabel label="Job Title" tooltip="" />
                    <AutocompleteFilter
                      color="primary"
                      isFullWidth
                      isMultiple
                      label="E.g. Business Development Manager"
                      limitTags={2}
                      name="jobTitles"
                      onChange={(newJobTitlesArray: string[]) => {
                        handleFilterChange({ jobTitles: newJobTitlesArray });
                      }}
                      size="small"
                      type="text"
                      value={filters.jobTitles}
                    />
                  </Box>
                  <Box sx={{ width: { md: "31%", xs: "100%" } }}>
                    <FilterFieldLabel label="Location" tooltip="" />
                    <AutocompleteFilter
                      color="primary"
                      isFullWidth
                      isMultiple
                      label="E.g. United States, Colorado, etc."
                      limitTags={2}
                      name="locations"
                      onChange={(newLocationsArray: string[]) => {
                        handleFilterChange({ locations: newLocationsArray });
                      }}
                      size="small"
                      type="text"
                      value={filters.locations}
                    />
                  </Box>
                  <Box sx={{ width: { md: "31%", xs: "100%" } }}>
                    <FilterFieldLabel label="Keywords" tooltip="" />
                    <AutocompleteFilter
                      color="primary"
                      isFullWidth
                      isMultiple
                      label="E.g. Partnerships, Strategic, etc."
                      limitTags={2}
                      name="keywords"
                      onChange={(newKeywordsArray: string[]) => {
                        handleFilterChange({ keywords: newKeywordsArray });
                      }}
                      size="small"
                      type="text"
                      value={filters.keywords}
                    />
                  </Box>
                  <Button type="submit" />
                </Box>
                <Box sx={FilterStyles.container}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    <FilterFieldLabel label="Has Attended Conference" tooltip="" />
                    <Checkbox
                      color="primary"
                      label={<Typography sx={FilterStyles.checkboxLabel}>Yes</Typography>}
                      name="hasAttendedConference"
                      onChange={() => handleFilterChange({ hasAttendedConference: !values.hasAttendedConference })}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0, ml: 2 }}>
                    <FilterFieldLabel label="Has Verified Email" tooltip="" />
                    <Checkbox
                      color="primary"
                      label={<Typography sx={FilterStyles.checkboxLabel}>Yes</Typography>}
                      name="hasVerifiedEmail"
                      onChange={() => handleFilterChange({ hasVerifiedEmail: !values.hasVerifiedEmail })}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Formik>
      </Drawer>
    );
  },
);
ContactFilterDrawer.displayName = "ContactFilterDrawer";

export default ContactFilterDrawer;
